import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: string;
    options: string[];
}


const FillBlanksBodyParts: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'FillBlanksBodyParts.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/FillBlanksBodyParts.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "Parts of Body Fill in the Blanks for UKG | Fun Worksheets for Kids"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: string) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help your UKG child learn body parts through engaging fill-in-the-blanks worksheets. Strengthen vocabulary and recognition with fun printable activities." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/parts-of-body/fill-the-blanks" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Parts of the Body</h1>

                                    <p>
                                        Understanding body parts is an essential life skill for UKG children. In this engaging <strong>fill-in-the-blanks</strong> activity, kids practice identifying and spelling common body parts. This boosts vocabulary, improves observation skills, and enhances their knowledge of the human body in a fun and simple way.
                                    </p>

                                    <h2>👧 Why Learn About Body Parts in UKG?</h2>
                                    <ul>
                                        <li>Develop self-awareness</li>
                                        <li>Build basic hygiene and safety understanding</li>
                                        <li>Strengthen communication using correct body part names</li>
                                    </ul>

                                    <h2>📝 Sample Fill in the Blanks Questions</h2>
                                    <ul>
                                        <li>We smell with our ________. (nose)</li>
                                        <li>I hear with my ________. (ears)</li>
                                        <li>We walk using our ________. (legs)</li>
                                        <li>We write using our ________. (hands)</li>
                                        <li>We see with our ________. (eyes)</li>
                                    </ul>



                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify major external body parts</li>
                                        <li>Use the correct spelling for each part</li>
                                        <li>Improve sentence completion skills</li>
                                    </ul>

                                    <h2>🧠 Tips for Parents and Teachers</h2>
                                    <ul>
                                        <li>Use body action songs to introduce body parts</li>
                                        <li>Let the child touch the part while filling in the blank</li>
                                        <li>Play games like “Simon Says” for reinforcement</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Parts of the Body</h1>

                                <p>
                                    Understanding body parts is an essential life skill for UKG children. In this engaging <strong>fill-in-the-blanks</strong> activity, kids practice identifying and spelling common body parts. This boosts vocabulary, improves observation skills, and enhances their knowledge of the human body in a fun and simple way.
                                </p>

                                <h2>👧 Why Learn About Body Parts in UKG?</h2>
                                <ul>
                                    <li>Develop self-awareness</li>
                                    <li>Build basic hygiene and safety understanding</li>
                                    <li>Strengthen communication using correct body part names</li>
                                </ul>

                                <h2>📝 Sample Fill in the Blanks Questions</h2>
                                <ul>
                                    <li>We smell with our ________. (nose)</li>
                                    <li>I hear with my ________. (ears)</li>
                                    <li>We walk using our ________. (legs)</li>
                                    <li>We write using our ________. (hands)</li>
                                    <li>We see with our ________. (eyes)</li>
                                </ul>



                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify major external body parts</li>
                                    <li>Use the correct spelling for each part</li>
                                    <li>Improve sentence completion skills</li>
                                </ul>

                                <h2>🧠 Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Use body action songs to introduce body parts</li>
                                    <li>Let the child touch the part while filling in the blank</li>
                                    <li>Play games like “Simon Says” for reinforcement</li>
                                </ul>
                            </article>
                            <div className="mt-2 mb-4">
                                <h3 className="mb-5">
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>
                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value)}>
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem key={index}
                                        className={`grid-item ${selectedOption === option
                                            ? option === questions[currentIndex]?.correct
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}>
                                        <IonRadio slot="start" value={option} />
                                        <IonLabel>{option}</IonLabel>
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default FillBlanksBodyParts;
